import { useState, useEffect, useMemo } from 'react';
import {
  Megaphone,
  Plus,
  Edit,
  Filter,
  Search,
  X,
  Play,
  Eye,
  Smartphone,
  Monitor,
  Loader2,
  Save,
  AlertCircle,
  CalendarDays,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAdminEventFilter } from '@/hooks/useAdminEventFilter';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';
import SponsorImageUpload from '@/components/SponsorImageUpload';

// ─── Types ────────────────────────────────────────────────────────────────────

type AdStatus = 'active' | 'scheduled';

interface SponsorEvent {
  id: string;
  name: string;
  status: string;
  dateStart: string;
  dateEnd: string;
}

interface Sponsor {
  id: string;
  eventId: string;
  name: string;
  adImageUrl: string | null;
  adStartDate: string | null;
  adEndDate: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  event?: SponsorEvent;
}

interface SponsorFormData {
  eventId: string;
  name: string;
  adImageUrl: string;
  adDate: string;
  adTime: string;
  isActive: boolean;
}

const DEFAULT_FORM: SponsorFormData = {
  eventId: '',
  name: '',
  adImageUrl: '',
  adDate: '',
  adTime: '',
  isActive: false,
};

const ALL_AD_STATUSES: AdStatus[] = ['active', 'scheduled'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getAdStatuses(sponsor: Sponsor): AdStatus[] {
  const statuses: AdStatus[] = [];
  if (sponsor.isActive) statuses.push('active');
  if (sponsor.adStartDate) statuses.push('scheduled');
  return statuses;
}

const AD_STATUS_BADGE: Record<AdStatus, string> = {
  active: 'bg-green-100 text-green-800 border-green-200',
  scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
};

const AD_STATUS_LABEL: Record<AdStatus, string> = {
  active: 'Active',
  scheduled: 'Scheduled',
};

function toDatePart(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

function toTimePart(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SponsorManagementPage() {
  const { eventFilter, setEventFilter, events, fetchEvents, resolvedEventId } = useAdminEventFilter();

  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSponsorId, setSelectedSponsorId] = useState<string | null>(null);
  const [sponsorDetail, setSponsorDetail] = useState<Sponsor | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterAdStatuses, setFilterAdStatuses] = useState<AdStatus[]>([]);

  // Form modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editSponsorId, setEditSponsorId] = useState<string | null>(null);
  const [formData, setFormData] = useState<SponsorFormData>(DEFAULT_FORM);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Ad preview and test push
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [testAdConfirmOpen, setTestAdConfirmOpen] = useState(false);
  const [testAdLoading, setTestAdLoading] = useState(false);

  useEffect(() => {
    fetchSponsors();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedSponsorId) {
      fetchSponsorDetail(selectedSponsorId);
    } else {
      setSponsorDetail(null);
    }
  }, [selectedSponsorId]);

  const fetchSponsors = async () => {
    try {
      const res = await api.get<{ success: boolean; data: Sponsor[] }>('/admin/sponsors');
      setSponsors(res.data ?? []);
    } catch {
      toast.error('Failed to load sponsors');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSponsorDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: Sponsor }>(`/admin/sponsors/${id}`);
      setSponsorDetail(res.data ?? null);
    } catch {
      toast.error('Failed to load sponsor details');
    } finally {
      setDetailLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditSponsorId(null);
    const activeEvent = events.find((e) => e.status === 'active');
    setFormData({ ...DEFAULT_FORM, eventId: activeEvent?.id ?? '' });
    setFormError(null);
    setModalOpen(true);
  };

  const openEditModal = (sponsor: Sponsor) => {
    setEditSponsorId(sponsor.id);
    setFormData({
      eventId: sponsor.eventId,
      name: sponsor.name,
      adImageUrl: sponsor.adImageUrl ?? '',
      adDate: toDatePart(sponsor.adStartDate),
      adTime: toTimePart(sponsor.adStartDate),
      isActive: sponsor.isActive,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSaving(true);

    const adDateTime = formData.adDate && formData.adTime
      ? new Date(`${formData.adDate}T${formData.adTime}`).toISOString()
      : '';

    const payload = {
      ...(!editSponsorId && { eventId: formData.eventId }),
      name: formData.name,
      adImageUrl: formData.adImageUrl,
      adStartDate: adDateTime,
      adEndDate: adDateTime,
      isActive: formData.isActive,
    };

    try {
      if (editSponsorId) {
        await api.patch(`/admin/sponsors/${editSponsorId}`, payload);
        toast.success('Sponsor updated');
      } else {
        await api.post('/admin/sponsors', payload);
        toast.success('Sponsor created');
      }
      setModalOpen(false);
      fetchSponsors();
      if (selectedSponsorId) fetchSponsorDetail(selectedSponsorId);
    } catch (error: any) {
      const msg = error.response?.data?.error ?? `Failed to ${editSponsorId ? 'update' : 'create'} sponsor`;
      setFormError(msg);
      toast.error(msg);
    } finally {
      setFormSaving(false);
    }
  };

  const handleTestAd = async () => {
    if (!selectedSponsorId) return;
    setTestAdLoading(true);
    try {
      await api.post(`/admin/sponsors/${selectedSponsorId}/test-ad`);
      toast.success('Ad pushed to all connected users');
      setTestAdConfirmOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? 'Failed to push test ad');
    } finally {
      setTestAdLoading(false);
    }
  };

  const toggleAdStatus = (status: AdStatus) =>
    setFilterAdStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );

  const clearFilters = () => {
    setFilterSearch('');
    setEventFilter('active');
    setFilterAdStatuses([]);
  };

  const filteredSponsors = useMemo(() => {
    return sponsors.filter((s) => {
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!s.name.toLowerCase().includes(q) && !s.event?.name.toLowerCase().includes(q)) return false;
      }
      if (resolvedEventId && s.eventId !== resolvedEventId) return false;
      const statuses = getAdStatuses(s);
      if (filterAdStatuses.length > 0 && !filterAdStatuses.some((f) => statuses.includes(f))) return false;
      return true;
    });
  }, [sponsors, filterSearch, resolvedEventId, filterAdStatuses]);

  // Clear selection when it's no longer in the visible list
  useEffect(() => {
    if (selectedSponsorId && !filteredSponsors.some((s) => s.id === selectedSponsorId)) {
      setSelectedSponsorId(null);
    }
  }, [filteredSponsors, selectedSponsorId]);

  const activeFilterCount = (filterSearch ? 1 : 0) + (eventFilter !== 'active' ? 1 : 0) + filterAdStatuses.length;
  const selectedSponsor = filteredSponsors.find((s) => s.id === selectedSponsorId) ?? null;
  const activeEvent = events.find((e) => e.status === 'active');

  // Get selected event date range for form validation
  const selectedFormEvent = events.find((e) => e.id === formData.eventId);
  const formEventDateStart = selectedFormEvent ? selectedFormEvent.dateStart.slice(0, 10) : '';
  const formEventDateEnd = selectedFormEvent ? selectedFormEvent.dateEnd.slice(0, 10) : '';

  // ─── Left Panel: Filters ──────────────────────────────────────────────────

  const FiltersPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Filter className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Filters</h2>
            <p className="text-sm text-muted-foreground">Refine your search</p>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs">
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Search */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Sponsor or event name..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                className="pl-9 h-9"
              />
              {filterSearch && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setFilterSearch('')}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          <Separator />

          {/* Event Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Event</Label>
            <RadioGroup
              value={eventFilter}
              onValueChange={(v) => { setEventFilter(v); setSelectedSponsorId(null); }}
              className="gap-0.5"
            >
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="all" id="event-all" className="h-3.5 w-3.5" />
                <label htmlFor="event-all" className="text-xs cursor-pointer">All Events</label>
              </div>
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="active" id="event-active" className="h-3.5 w-3.5" />
                <label htmlFor="event-active" className="text-xs cursor-pointer">Active Event</label>
              </div>
              {events
                .filter((ev) => ev.status === 'archived')
                .sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime())
                .map((ev) => (
                  <div key={ev.id} className="flex items-center gap-1.5 py-0.5">
                    <RadioGroupItem value={ev.id} id={`event-${ev.id}`} className="h-3.5 w-3.5" />
                    <label htmlFor={`event-${ev.id}`} className="text-xs cursor-pointer flex items-center justify-between w-full">
                      <span className="truncate">{ev.name}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-1">
                        {new Date(ev.dateStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    </label>
                  </div>
                ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Ad Status Filter */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Ad Status</Label>
            <div className="space-y-2">
              {ALL_AD_STATUSES.map((status) => (
                <div key={status} className="flex items-center gap-2">
                  <Checkbox
                    id={`ad-status-${status}`}
                    checked={filterAdStatuses.includes(status)}
                    onCheckedChange={() => toggleAdStatus(status)}
                  />
                  <label
                    htmlFor={`ad-status-${status}`}
                    className="text-sm cursor-pointer flex items-center gap-2"
                  >
                    <Badge variant="outline" className={cn('w-20 justify-center px-2.5 py-0.5 text-xs font-medium', AD_STATUS_BADGE[status])}>
                      {AD_STATUS_LABEL[status]}
                    </Badge>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );

  // ─── Center Panel: Sponsor List ───────────────────────────────────────────

  const SponsorRowSkeleton = () => (
    <div className="px-4 py-3 border-b border-border">
      <Skeleton className="h-4 w-40 mb-2" />
      <Skeleton className="h-3 w-32" />
    </div>
  );

  const CenterPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Megaphone className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Sponsors</h2>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading...' : `${filteredSponsors.length} of ${sponsors.length} sponsor${sponsors.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="gap-1.5" onClick={openCreateModal} disabled={!activeEvent} title={!activeEvent ? 'No active event' : undefined}>
            <Plus className="h-4 w-4" />
            New Sponsor
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div>
            {Array.from({ length: 4 }).map((_, i) => <SponsorRowSkeleton key={i} />)}
          </div>
        ) : filteredSponsors.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No sponsors found</p>
            {sponsors.length > 0 && (
              <p className="text-sm mt-1">Try adjusting your filters</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredSponsors.map((sponsor) => {
              const adStatuses = getAdStatuses(sponsor);
              return (
                <div
                  key={sponsor.id}
                  onClick={() => setSelectedSponsorId(sponsor.id === selectedSponsorId ? null : sponsor.id)}
                  className={cn(
                    'px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 flex items-center justify-between gap-3',
                    selectedSponsorId === sponsor.id && 'bg-primary/5 border-l-2 border-l-primary',
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{sponsor.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {sponsor.event?.name ?? 'Unknown event'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {adStatuses.map((s) => (
                      <Badge key={s} variant="outline" className={cn('px-2 py-0 text-[10px] font-medium', AD_STATUS_BADGE[s])}>
                        {AD_STATUS_LABEL[s]}
                      </Badge>
                    ))}
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openEditModal(sponsor)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit sponsor"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ─── Right Panel: Sponsor Detail ──────────────────────────────────────────

  const RightPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Megaphone className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Sponsor Details</h2>
            <p className="text-sm text-muted-foreground">
              {selectedSponsor ? selectedSponsor.name : 'Select a sponsor'}
            </p>
          </div>
        </div>
      </div>

      {!selectedSponsorId ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground p-8">
          <div className="text-center">
            <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Select a sponsor</p>
            <p className="text-sm mt-1">Click any row to view full details</p>
          </div>
        </div>
      ) : detailLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : sponsorDetail ? (
        <ScrollArea className="flex-1">
          <div className="p-5 space-y-5">
            {/* Header */}
            <div>
              <h3 className="text-xl font-bold">{sponsorDetail.name}</h3>
              {getAdStatuses(sponsorDetail).length > 0 && (
                <div className="flex items-center gap-2 mt-1.5">
                  {getAdStatuses(sponsorDetail).map((s) => (
                    <Badge key={s} variant="outline" className={cn('px-2.5 py-0.5 text-xs font-medium', AD_STATUS_BADGE[s])}>
                      {AD_STATUS_LABEL[s]}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Event Info */}
            {sponsorDetail.event && (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <CalendarDays className="h-4 w-4 shrink-0" />
                <span>{sponsorDetail.event.name}</span>
              </div>
            )}

            {/* Ad Delivery */}
            {sponsorDetail.adStartDate && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-1.5">Ad Delivery</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDateTime(sponsorDetail.adStartDate)}
                  </p>
                </div>
              </>
            )}

            {/* Ad Image */}
            {sponsorDetail.adImageUrl && (
              <>
                <Separator />
                <div className="rounded-lg border border-border overflow-hidden">
                  <img
                    src={sponsorDetail.adImageUrl}
                    alt={`${sponsorDetail.name} ad`}
                    className="w-full object-contain"
                  />
                </div>
              </>
            )}

            {/* Actions */}
            <Separator />
            <div className="flex items-center gap-2">
              {sponsorDetail.adImageUrl && (
                <Button size="sm" variant="default" className="gap-1.5" onClick={() => setPreviewOpen(true)}>
                  <Eye className="h-4 w-4" />
                  Preview
                </Button>
              )}
              <Button size="sm" variant="default" className="gap-1.5" onClick={() => openEditModal(sponsorDetail)}>
                <Edit className="h-4 w-4" />
                Edit
              </Button>
              {sponsorDetail.adImageUrl && sponsorDetail.event?.status === 'active' && (
                <Button size="sm" variant="default" className="gap-1.5" onClick={() => setTestAdConfirmOpen(true)}>
                  <Play className="h-4 w-4" />
                  Test
                </Button>
              )}
            </div>
          </div>
        </ScrollArea>
      ) : null}
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <DesktopShell>
        <div className="h-full flex flex-col">
          <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-background to-muted/30 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Megaphone className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Sponsor Management</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Manage event sponsors and advertising
                </p>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <ThreePanelLayout
              left={FiltersPanel}
              center={CenterPanel}
              right={RightPanel}
              leftWidth="260px"
              equalCenterRight
              connectedPanels={!!selectedSponsor}
            />
          </div>
        </div>
      </DesktopShell>

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => !formSaving && setModalOpen(open)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editSponsorId ? 'Edit Sponsor' : 'Create Sponsor'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleFormSubmit} className="space-y-4 pt-2">
            {formError && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            {/* Event — auto-assigned to active event on create, read-only display */}
            {!editSponsorId && selectedFormEvent && (
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Event</Label>
                <p className="text-sm text-muted-foreground">{selectedFormEvent.name}</p>
              </div>
            )}

            <div>
              <Label htmlFor="sp-name" className="text-sm font-medium mb-1.5 block">Sponsor Name *</Label>
              <Input
                id="sp-name"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                required
                minLength={1}
                maxLength={100}
                placeholder="e.g., Lockheed Martin"
              />
            </div>

            <SponsorImageUpload
              value={formData.adImageUrl}
              onChange={(base64) => setFormData((p) => ({ ...p, adImageUrl: base64 }))}
              width={1200}
              height={675}
              label="Ad Image (16:9)"
              sizeHint="1200x675px, JPEG or PNG"
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Ad Delivery Date</Label>
                <Input
                  type="date"
                  value={formData.adDate}
                  onChange={(e) => setFormData((p) => ({ ...p, adDate: e.target.value, ...(!e.target.value && { isActive: false }) }))}
                  min={formEventDateStart}
                  max={formEventDateEnd}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Ad Delivery Time</Label>
                <Input
                  type="time"
                  value={formData.adTime}
                  onChange={(e) => setFormData((p) => ({ ...p, adTime: e.target.value, ...(!e.target.value && { isActive: false }) }))}
                />
              </div>
            </div>

            <Separator />

            <div className="flex items-center gap-2">
              <Checkbox
                id="sp-active"
                checked={formData.isActive}
                disabled={!formData.adDate || !formData.adTime}
                onCheckedChange={(checked) => setFormData((p) => ({ ...p, isActive: !!checked }))}
              />
              <Label htmlFor="sp-active" className={cn('text-sm font-medium cursor-pointer', (!formData.adDate || !formData.adTime) && 'text-muted-foreground')}>
                Active
              </Label>
              {(!formData.adDate || !formData.adTime) ? (
                <span className="text-xs text-muted-foreground">— set delivery date and time first</span>
              ) : (
                <span className="text-xs text-muted-foreground">— sponsor ad is live for this event</span>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} disabled={formSaving}>
                Cancel
              </Button>
              <Button type="submit" disabled={formSaving || (!editSponsorId && !formData.eventId)} className="gap-2">
                {formSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {editSponsorId ? 'Update' : 'Create'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Ad Confirmation */}
      <AlertDialog
        open={testAdConfirmOpen}
        onOpenChange={(open) => !testAdLoading && !open && setTestAdConfirmOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Push Ad to All Users?</AlertDialogTitle>
            <AlertDialogDescription>
              This will display the sponsor popup immediately on the home screen of <strong>all currently connected users</strong>
              (checked-in attendees, staff, and admins). This bypasses the daily schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={testAdLoading}>Cancel</AlertDialogCancel>
            <Button onClick={handleTestAd} disabled={testAdLoading} className="gap-2">
              {testAdLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Pushing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Push Ad Now
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ad Preview — device-framed local preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Ad Preview — {sponsorDetail?.name}</span>
              <div className="flex items-center gap-1 border border-border rounded-lg p-0.5">
                <Button
                  variant={previewDevice === 'mobile' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 px-2.5 gap-1 text-xs"
                  onClick={() => setPreviewDevice('mobile')}
                >
                  <Smartphone className="h-3.5 w-3.5" />
                  Mobile
                </Button>
                <Button
                  variant={previewDevice === 'desktop' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 px-2.5 gap-1 text-xs"
                  onClick={() => setPreviewDevice('desktop')}
                >
                  <Monitor className="h-3.5 w-3.5" />
                  Desktop
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {sponsorDetail?.adImageUrl && (
            <div className="flex justify-center py-4">
              <div
                className={cn(
                  'border-[3px] border-foreground/20 rounded-2xl overflow-hidden shadow-xl bg-background',
                  previewDevice === 'mobile' ? 'w-[320px]' : 'w-[500px]'
                )}
              >
                <div className="flex flex-col">
                  {/* NPS header */}
                  <div className="relative bg-gradient-to-br from-[hsl(216,100%,15%)] via-[hsl(216,80%,22%)] to-[hsl(195,80%,30%)] px-4 py-3">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
                    <div className="relative flex items-center gap-2">
                      <img src="/nps-logo.png" alt="NPS" className="h-8 w-auto" />
                      <div>
                        <p className="text-sm font-bold text-white">Converge @ NPS</p>
                        <p className="text-[9px] font-medium text-[#c5a44e]">Naval Postgraduate School</p>
                      </div>
                    </div>
                  </div>

                  {/* Ad image */}
                  <div className="flex items-center justify-center bg-muted/30">
                    <img
                      src={sponsorDetail.adImageUrl}
                      alt="Ad preview"
                      className="w-full object-contain"
                    />
                  </div>

                  {/* Sponsor footer */}
                  <div className="bg-background border-t border-border px-4 py-3 text-center">
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Proud Sponsor</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
